import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, ArrowUpRight, Sparkles, Building2, Route, FlaskConical } from 'lucide-react';
import { usePopups } from '../data';
import './promo.css';

/*
 * Promo popup engine (admin-driven). Each popup document carries its own content,
 * target pages, trigger, design and per-visitor frequency. This component lives
 * once inside the router, watches the route, and schedules the first matching
 * popup for the current page. Designs adapt to the page they open on.
 *
 *  trigger:  load (right after intro)  | delay (after N seconds) | interval (every N minutes)
 *  design:   spotlight | card | corner | banner
 *  frequency: always | session | once   (interval ignores the cap — it repeats)
 */

const PAGE_META = {
  '/': { Icon: Sparkles, label: 'Moab Engineering' },
  '/structural': { Icon: Building2, label: 'Structural' },
  '/infrastructure': { Icon: Route, label: 'Infrastructure' },
  '/research': { Icon: FlaskConical, label: 'Research' },
};

function pageKeyOf(pathname) {
  if (pathname === '/') return '/';
  if (pathname.startsWith('/structural')) return '/structural';
  if (pathname.startsWith('/infrastructure')) return '/infrastructure';
  if (pathname.startsWith('/research')) return '/research';
  return null; // admin / 404 — no popups
}

function matchesPage(p, pageKey) {
  const pages = Array.isArray(p.pages) && p.pages.length ? p.pages : ['all'];
  return pages.includes('all') || pages.includes(pageKey);
}

// Wait for the branded intro to finish on the first visit so the popup never
// lands on top of the preloader.
function introOffset() {
  try {
    return sessionStorage.getItem('moab-intro') === '1' ? 0 : 3500;
  } catch {
    return 0;
  }
}

function capKey(p) {
  return `moab-promo-${p.id || p.title || 'x'}`;
}
function canShow(p) {
  try {
    if (p.frequency === 'once') return localStorage.getItem(capKey(p)) !== '1';
    if (p.frequency === 'session' || !p.frequency) return sessionStorage.getItem(capKey(p)) !== '1';
  } catch {
    /* storage blocked — show anyway */
  }
  return true; // 'always'
}
function markSeen(p) {
  try {
    if (p.frequency === 'once') localStorage.setItem(capKey(p), '1');
    else if (p.frequency === 'session' || !p.frequency) sessionStorage.setItem(capKey(p), '1');
  } catch {
    /* ignore */
  }
}

export default function PromoManager() {
  const popups = usePopups();
  const { pathname } = useLocation();
  const [active, setActive] = useState(null);

  useEffect(() => {
    setActive(null);
    const pageKey = pageKeyOf(pathname);
    if (!pageKey) return undefined;

    const candidate = (popups || [])
      .filter((p) => p.enabled !== false && matchesPage(p, pageKey))
      .sort((a, b) => (a.order || 0) - (b.order || 0))[0];
    if (!candidate) return undefined;

    const cleanups = [];
    const trigger = candidate.trigger || 'load';

    if (trigger === 'interval') {
      // Repeats — frequency cap does not apply; never replace an open popup.
      const ms = Math.max(0.2, Number(candidate.intervalMinutes) || 5) * 60000;
      const iv = setInterval(() => setActive((cur) => cur || candidate), ms);
      cleanups.push(() => clearInterval(iv));
    } else {
      if (!canShow(candidate)) return undefined;
      const wait =
        introOffset() + (trigger === 'delay' ? Math.max(0, Number(candidate.delaySeconds) || 0) * 1000 : 600);
      const t = setTimeout(() => {
        setActive((cur) => {
          if (cur) return cur;
          markSeen(candidate);
          return candidate;
        });
      }, wait);
      cleanups.push(() => clearTimeout(t));
    }

    return () => cleanups.forEach((fn) => fn());
  }, [pathname, popups]);

  if (!active) return null;
  return <PromoPopup popup={active} pageKey={pageKeyOf(pathname)} onClose={() => setActive(null)} />;
}

function PromoPopup({ popup, pageKey, onClose }) {
  const design = popup.design || 'spotlight';
  const modal = design === 'spotlight' || design === 'card';

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    if (modal) document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, modal]);

  const meta = PAGE_META[pageKey] || PAGE_META['/'];
  const Icon = meta.Icon;
  const hasImg = !!popup.image;

  const badge = (
    <span className="promo__badge">
      <Icon size={13} /> {meta.label}
    </span>
  );
  const cta = popup.linkHref ? (
    <a className="btn btn--accent promo__cta" href={popup.linkHref} onClick={onClose}>
      {popup.linkLabel || 'Learn more'} <ArrowUpRight size={16} />
    </a>
  ) : null;

  let inner;
  if (design === 'spotlight') {
    inner = (
      <>
        <div className="promo__media">
          {hasImg ? <img src={popup.image} alt="" /> : <div className="promo__media-fallback"><Icon size={56} /></div>}
          <span className="promo__scan" aria-hidden="true" />
          <span className="promo__media-glow" aria-hidden="true" />
        </div>
        <div className="promo__content">
          {badge}
          <h2 className="promo__title">{popup.title}</h2>
          {popup.body && <p className="promo__text">{popup.body}</p>}
          {cta}
        </div>
      </>
    );
  } else if (design === 'banner') {
    inner = (
      <div className="promo__banner-inner">
        {hasImg && <img className="promo__banner-thumb" src={popup.image} alt="" />}
        <div className="promo__banner-text">
          {badge}
          <strong className="promo__title">{popup.title}</strong>
          {popup.body && <span className="promo__text">{popup.body}</span>}
        </div>
        {cta}
      </div>
    );
  } else {
    // card + corner share a stacked layout
    inner = (
      <>
        {hasImg && (
          <div className="promo__thumb">
            <img src={popup.image} alt="" />
          </div>
        )}
        <div className="promo__content">
          {badge}
          <h2 className="promo__title">{popup.title}</h2>
          {popup.body && <p className="promo__text">{popup.body}</p>}
          {cta}
        </div>
      </>
    );
  }

  return (
    <div className={`promo promo--${design}`} data-page={pageKey || 'site'}>
      {modal && <div className="promo__backdrop" onClick={onClose} aria-hidden="true" />}
      <div
        className="promo__shell"
        role="dialog"
        aria-modal={modal}
        aria-label={popup.title}
      >
        <button className="promo__close" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
        {inner}
      </div>
    </div>
  );
}
